import { useEffect, useRef } from 'react'
import { MODAL_TITLE_ID } from './ModalTitle'
import type { MouseEvent, ReactNode, SyntheticEvent } from 'react'

interface PropsType {
  isOpen: boolean
  onRequestClose: () => void
  children: ReactNode
}

// showModal非対応ブラウザで例外を投げると検索パネルのReact rootごと消えるため、open属性で代替する
const openDialog = (dialog: HTMLDialogElement) => {
  if (typeof dialog.showModal === 'function') {
    dialog.showModal()
  } else {
    dialog.setAttribute('open', '')
  }
}

const closeDialog = (dialog: HTMLDialogElement) => {
  if (typeof dialog.close === 'function') {
    dialog.close()
  } else {
    dialog.removeAttribute('open')
  }
}

/**
 * 検索パネルの条件選択モーダル共通のダイアログ（react-modalの代替）
 *
 * 【仕組み】ネイティブ<dialog>のshowModal()で開くと、フォーカストラップと
 * 背景の操作無効化（inert）をブラウザが担う。Escキーはcancelイベント、
 * オーバーレイ（::backdrop）クリックはdialog自身へのclickとして検知し、
 * どちらもReact側のstate（isOpen）経由で閉じることで状態のズレを防ぐ。
 */
const SelectModalDialog = (props: PropsType) => {
  const { isOpen, onRequestClose, children } = props
  const dialogRef = useRef<HTMLDialogElement>(null)
  const pressedOnOverlayRef = useRef(false)

  useEffect(() => {
    const dialog = dialogRef.current
    if (dialog === null) return

    // 非対応ブラウザでは open プロパティが無いため属性で判定する
    const isShown = dialog.hasAttribute('open')
    if (isOpen && !isShown) {
      openDialog(dialog)
      // 背景ページのスクロールロック（旧実装の .ReactModal__Body--open 相当）
      document.body.style.overflow = 'hidden'
    } else if (!isOpen && isShown) {
      closeDialog(dialog)
    }

    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  const handleCancel = (e: SyntheticEvent<HTMLDialogElement>) => {
    e.preventDefault()
    onRequestClose()
  }

  const handleMouseDown = (e: MouseEvent<HTMLDialogElement>) => {
    pressedOnOverlayRef.current = e.target === dialogRef.current
  }

  const handleClick = (e: MouseEvent<HTMLDialogElement>) => {
    // 内容部分のクリックは子要素がtargetになるため、dialog自身ならオーバーレイのクリック。
    // 中身で押して枠外で離した場合もtargetがdialogになるため、押した位置もオーバーレイのときに限る
    if (pressedOnOverlayRef.current && e.target === dialogRef.current) {
      onRequestClose()
    }
  }

  return (
    <dialog
      ref={dialogRef}
      // スクリーンリーダーに条件名（「職種を選択」等）を読ませるため、中身のModalTitleを名前として参照する
      aria-labelledby={MODAL_TITLE_ID}
      onCancel={handleCancel}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      // 位置・サイズと色は旧react-modalのスタイルを踏襲（閉じるボタンが枠外に出るためoverflow-visible）。
      // h-autoはdialogの既定値 height:fit-content の打ち消し。これがないと上下の位置指定で高さが決まらず、
      // 中身のheight:100%が解決できずモーダル内のabsolute配置（フッター・一覧）が崩れる
      className="fixed top-[5%] bottom-[5%] mx-auto my-0 h-auto max-h-[90%] min-h-100 w-[750px] max-w-none overflow-visible rounded border border-gray-500 bg-white p-0 backdrop:bg-gray-900/80"
    >
      {isOpen && children}
    </dialog>
  )
}

export default SelectModalDialog
